import { postForm, type FormFields } from '@/shared/api/client';
import { submitForm } from '@/shared/api/submit';
import { getListRows } from '@/shared/lib/pagination';
import type { SelectOption } from '@/shared/ui/selectTypes';

export const EXPORT_STATUS = {
  PLANNED: 0,
  IN_PROGRESS: 1,
  DONE: 2,
  FAILED: 3,
  CANCELLED: 4,
} as const;

export interface ExportFilter {
  country_id?: number | string;
  from_time?: number | string;
  to_time?: number | string;
  promocode?: string | string[];
  utm_source?: string | string[];
}

export interface ExportInfo {
  id: number | string;
  status_id: number;
  create_time?: number | string;
  progress?: string;
  filter?: ExportFilter | null;
}

/** Whether the last export is planned, running or ready to download (so the next create is a re-create). */
export const hasExportResult = (info: ExportInfo | null) =>
  info !== null &&
  [EXPORT_STATUS.PLANNED, EXPORT_STATUS.IN_PROGRESS, EXPORT_STATUS.DONE].some((s) => s === info.status_id);

/** `null` when nothing has been exported yet. */
export async function fetchLastExport(path: string): Promise<ExportInfo | null> {
  const res = await postForm<{ success?: number; last_export?: ExportInfo | null }>(`/export/${path}/`);
  return res.success === 1 ? (res.last_export ?? null) : null;
}

export async function createExport(path: string, params: FormFields): Promise<ExportInfo> {
  const res = await submitForm<{ success: number; export: ExportInfo }>(`/export/${path}/create/`, { ...params });
  return res.export;
}

export function cancelExport(path: string, exportId: number | string) {
  return postForm(`/export/${path}/cancel/`, { export_id: String(exportId) });
}

export function downloadExport(path: string) {
  return postForm<Blob>(`/export/${path}/download/`, {}, { responseType: 'blob' });
}

const toUtmSource = (item: unknown): string => {
  if (item && typeof item === 'object') {
    const { name, id } = item as { name?: unknown; id?: unknown };
    return String(name ?? id);
  }
  return String(item);
};

/** UTM sources for loyalty exports; the backend filters by the plain source string. */
export async function fetchUtmSourceOptions(): Promise<SelectOption[]> {
  const res = await postForm<Record<string, unknown>>('/utm/list/');
  return getListRows<Record<string, unknown>>(res, ['items']).map((item) => {
    const source = toUtmSource(item);
    return { value: source, label: source };
  });
}

/** Loyalty promo codes (`promo_id=1` is legacy `PROD_PROMOCODE`); the backend filters by code. */
export async function fetchPromoCodeOptions(): Promise<SelectOption[]> {
  const res = await postForm<Record<string, unknown>>('/billing/billing-promo-code/index/', {
    page: 1,
    promo_id: 1,
  });
  return getListRows<{ code?: string }>(res, ['items'])
    .filter((item) => item.code)
    .map((item) => ({ value: String(item.code), label: String(item.code) }));
}
